import { h } from '@omni-torn/ui';
import { findLogCategory, inRange, rangeText, type DateRange } from '@omni-torn/shared';
import { forgetLogText, readingLogText } from '../own-history';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import {
  buildTrips,
  buysCsvRows,
  byCountry,
  byItem,
  flightsPerDay,
  profitPerDay,
  readTravelLog,
  sumBuys,
  timeThere,
  travelTotals,
  tripAt,
  tripsCsvRows,
  type Place,
  type TravelHistory,
  type Trip,
} from '@omni-torn/shared/travel-report';
import { firstRead, LogReader, MultiReader, type LogReaderLike, type LogStream } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { session, torn } from '../session';
import { box, duration, gated, loading, money, num, problem, short, signed, table, tct } from '../ui';
import { ref } from './torn';

/**
 * Travel → History (website roadmap S29 — torn.report's travel page): your trips per country, what you bought abroad,
 * its worth and estimated profit, flights per day — from your own log (Full key), read into this browser by the
 * log-report kit (S09): Torn's "Travel" log category (found by name in `torn/logcategories`, plus a category named for
 * buying abroad if Torn has one), else the whole log. Torn calls per visit: the categories (kept a day) and up to 3 pages
 * of each stream (the newest, back to what's kept), then only on "Read newer/older". Items' names and market values come
 * from the Omni-Torn server's copy of Torn's items (none of your calls). Your own key and log only, in your browser;
 * nothing is sent to or kept on the Omni-Torn server.
 * Torn doesn't document the travel lines: packages/shared travel-report.ts reads them leniently and never guesses.
 */

const LOG_NEED = { level: 4, user: ['log'] } as const;
const DAY = 86_400_000;

const tile = (label: string, value: string, data = '', cls = '', title = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}), ...(title ? { title } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: `tile-value ${cls}`.trim() }, value));
const tone = (n: number | null) => (n === null || n === 0 ? '' : n > 0 ? 't-green' : 't-red');
const moneySigned = (n: number | null) => signed(n === null ? null : Math.round(n), (x) => money(x));
const hours = (sec: number) => (sec ? duration(sec) : '—');

export async function renderTravelHistory(body: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  body.replaceChildren(gated('Your travel history', LOG_NEED, 'your log shows every flight and what you bought abroad.', () => build(me.playerId)));
}

function build(pid: number): HTMLElement {
  const q = new URLSearchParams(location.search);
  let range: DateRange = rangeFromAddress('30d');
  let country: string | null = q.get('country') || null;
  let items = new Map<number, RefItem>();
  const name = (id: number) => items.get(id)?.name ?? `Item ${id}`;
  const valueOf = (id: number) => items.get(id)?.marketValue ?? null;
  const itemLink = (id: number) => h('a', { href: `/market/${id}`, 'data-link': true }, name(id));
  const placeText = (p: Place | null) => p?.name ?? h('span', { class: 'muted' }, 'not said');
  const setQ = () => setAddressQuery(range, { tab: 'history', country });

  let store: LogStore | null = null;
  let reader: LogReaderLike | null = null;
  let first: Promise<void> | null = null;
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });
  const out = h('div', { 'data-travel-history': '' }, loading(readingLogText()));

  async function ensureReader(): Promise<LogReaderLike> {
    if (reader) return reader;
    store = await logStore(pid);
    // Torn's "Travel" log category by name (and one for buying abroad, if Torn has it); without it, the whole log.
    let cats: number[] = [];
    try {
      const list = await torn('/v2/torn/logcategories', {}, DAY);
      const travel = findLogCategory(list, /^travel(l?ing)?$/i);
      const abroad = findLogCategory(list, /abroad/i);
      cats = travel ? [travel, ...(abroad && abroad !== travel ? [abroad] : [])] : [];
    } catch {
      /* the whole log */
    }
    const streams: LogStream[] = cats.length ? cats.map((c) => ({ id: `cat:${c}`, cat: c })) : [{ id: 'all' }];
    reader = streams.length === 1 ? new LogReader(store, streams[0]!) : new MultiReader(streams.map((s) => new LogReader(store!, s)));
    return reader;
  }

  async function fill(): Promise<void> {
    const r = await ensureReader();
    const reread = () => void fill();
    const onRange = (nr: DateRange) => {
      range = nr;
      setQ();
      reread();
    };
    // Every travel line kept here (whatever stream or page read it), into trips.
    const events = readTravelLog(await store!.range({ from: null, to: null }));
    const whole = buildTrips(events);
    const kept = await keptLogPanel({
      reader: r,
      store: store!,
      range,
      matching: events.length,
      what: 'travel lines (flights, landings, buys abroad)',
      note: logNote,
      forgetText: forgetLogText(),
      onForget: () => {
        reader = null;
        store = null;
        first = null;
      },
      redraw: reread,
    });

    // Country filter: the places your trips and buys name.
    const places = new Map<string, Place>();
    for (const t of whole.trips) if (t.country) places.set(t.country.key, t.country);
    for (const b of whole.buys) if (b.place) places.set(b.place.key, b.place);
    if (country && !places.has(country)) country = null;
    const hist: TravelHistory = country
      ? {
          trips: whole.trips.filter((t) => t.country?.key === country),
          buys: whole.buys.filter((b) => b.place?.key === country),
          flights: whole.flights.filter((f) => f.to?.key === country || f.from?.key === country),
          other: [],
        }
      : whole;
    const filters = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Country', [['', 'Every country'], ...[...places.values()].sort((a, b) => a.name.localeCompare(b.name)).map((p) => [p.key, p.name] as const)], country ?? '', (v) => {
        country = v || null;
        setQ();
        reread();
      }, 'country'),
    );

    const t = travelTotals(hist, range, valueOf);
    const tiles = h(
      'div',
      { class: 'tiles' },
      tile('Trips', num(t.trips), 'trips'),
      tile('Flights', num(t.flights), 'flights', '', 'Every flight that left, out and back'),
      tile('Bought abroad', `${num(t.units)} for ${money(t.spent)}`, 'bought'),
      tile('Worth today (est.)', money(t.worth), 'worth', '', 'At Torn’s market value today'),
      tile('Profit (est.)', moneySigned(t.net), 'profit', tone(t.net), 'Worth today − what you paid − the tickets your log shows'),
      tile('In the air', hours(t.airSec), 'air'),
      tile('Abroad', hours(t.thereSec), 'there'),
    );
    const now = Date.now();
    const flightsChart = box(h('h2', {}, 'Flights per day'), zoomChart(flightsPerDay(hist, range, now), { label: 'Flights', format: (x) => (Number.isInteger(x) ? num(x) : ''), onZoom: onRange }), { id: 'travel-flights-chart' });
    const profitChart = box(h('h2', {}, 'Profit per day (est.)'), zoomChart(profitPerDay(hist, range, now, valueOf), { label: 'Profit (est.)', format: (x) => `${x < 0 ? '−' : ''}$${short(Math.abs(x))}`, onZoom: onRange }), { id: 'travel-profit-chart' });

    const countries = byCountry(hist, range, valueOf);
    const countryBox = box('Trips per country', [
      table(countries, [
        { label: 'Country', get: (x) => placeText(x.place), sort: (x) => x.place?.name ?? '~' },
        { label: 'Trips', get: (x) => num(x.trips), sort: (x) => x.trips, cls: 'r' },
        { label: 'Items', get: (x) => (x.units ? num(x.units) : '—'), sort: (x) => x.units, cls: 'r' },
        { label: 'Spent', get: (x) => (x.spent ? money(x.spent) : '—'), sort: (x) => x.spent, cls: 'r' },
        { label: 'Worth today', get: (x) => (x.worth ? money(x.worth) : '—'), sort: (x) => x.worth, cls: 'r' },
        { label: 'Tickets', get: (x) => (x.tickets ? money(x.tickets) : '—'), sort: (x) => x.tickets, cls: 'r' },
        { label: 'Profit (est.)', get: (x) => (x.net === null ? h('span', { class: 'muted' }, '—') : h('span', { class: tone(x.net) }, moneySigned(x.net))), sort: (x) => x.net ?? -Infinity, cls: 'r' },
        { label: 'Time there', get: (x) => (x.avgThere === null ? '—' : `~${duration(x.avgThere)}`), sort: (x) => x.avgThere ?? -1, cls: 'r' },
        { label: 'Last', get: (x) => (x.last ? tct(x.last) : '—'), sort: (x) => x.last },
      ], { initial: 1, empty: 'No trips in this range.' }),
    ], { id: 'travel-countries' });

    const itemRows = byItem(hist.buys, range, valueOf);
    const inDates = hist.buys.filter((b) => inRange(b.at, range));
    const buysCsv = csvButton(() => `travel-buys-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => buysCsvRows(inDates, valueOf, name));
    const itemBox = box('Bought abroad', [
      table(itemRows, [
        { label: 'Item', get: (x) => itemLink(x.itemId), sort: (x) => name(x.itemId) },
        { label: 'Where', get: (x) => x.places.map((p) => p.name).join(', ') || h('span', { class: 'muted' }, 'not said') },
        { label: 'Bought', get: (x) => num(x.units), sort: (x) => x.units, cls: 'r' },
        { label: 'Avg paid', get: (x) => money(x.avgCost), sort: (x) => x.avgCost ?? -1, cls: 'r' },
        { label: 'Value today', get: (x) => money(x.value), sort: (x) => x.value ?? -1, cls: 'r' },
        { label: 'Spent', get: (x) => money(x.spent), sort: (x) => x.spent, cls: 'r' },
        { label: 'Profit (est.)', get: (x) => (x.profit === null ? h('span', { class: 'muted' }, '—') : h('span', { class: tone(x.profit) }, moneySigned(x.profit))), sort: (x) => x.profit ?? -Infinity, cls: 'r' },
      ], { initial: 5, empty: 'Nothing bought abroad in this range.' }),
      t.unpriced || t.unvalued ? h('p', { class: 'muted small', 'data-uncounted': '' }, [t.unpriced ? `${num(t.unpriced)} bought at a price your log doesn’t show` : '', t.unvalued ? `${num(t.unvalued)} without a market value today` : ''].filter(Boolean).join(' · '), ' — left out of the profit, never guessed.') : null,
    ].filter(Boolean) as HTMLElement[], { id: 'travel-items', extra: buysCsv });

    const trips = hist.trips.filter((x) => inRange(tripAt(x), range)).reverse();
    const tripsCsv = csvButton(() => `travel-trips-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => tripsCsvRows(trips, valueOf, name));
    const tripProfit = (x: Trip) => {
      const s = sumBuys(x.buys, valueOf);
      return s.profit === null ? (x.tickets ? -x.tickets : null) : s.profit - x.tickets;
    };
    const tripsBox = box('Each trip', [
      table(trips.slice(0, 500), [
        { label: 'Left', get: (x) => (x.leftAt ? h('span', { title: new Date(x.leftAt).toUTCString() }, tct(x.leftAt)) : h('span', { class: 'muted', title: 'Before the kept log' }, '—')), sort: (x) => tripAt(x) },
        { label: 'To', get: (x) => placeText(x.country), sort: (x) => x.country?.name ?? '~' },
        { label: 'Flight', get: (x) => [x.method, x.flightOut ? duration(x.flightOut) : null].filter(Boolean).join(' · ') || '—' },
        { label: 'There', get: (x) => (timeThere(x) === null ? '—' : duration(timeThere(x))), sort: (x) => timeThere(x) ?? -1, cls: 'r' },
        { label: 'Bought', get: (x) => x.buys.map((b) => `${num(b.qty)}× ${name(b.itemId)}`).join(', ') || '—' },
        { label: 'Spent', get: (x) => (x.buys.length ? money(sumBuys(x.buys, valueOf).spent) : '—'), sort: (x) => sumBuys(x.buys, valueOf).spent, cls: 'r' },
        { label: 'Profit (est.)', get: (x) => (tripProfit(x) === null ? h('span', { class: 'muted' }, '—') : h('span', { class: tone(tripProfit(x)) }, moneySigned(tripProfit(x)))), sort: (x) => tripProfit(x) ?? -Infinity, cls: 'r' },
        { label: 'Home', get: (x) => (x.homeAt ? tct(x.homeAt) : x.backAt ? h('span', { class: 'muted' }, 'flying back') : x.landedAt || x.leftAt ? h('span', { class: 'muted' }, 'not yet') : '—') },
      ], { csv: false, initial: 0, empty: 'No trips in this range.' }),
      trips.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(trips.length)} — the CSV has them all.`) : null,
    ].filter(Boolean) as HTMLElement[], { id: 'travel-trips', extra: tripsCsv });

    const odd = (country ? [] : whole.other).filter((e) => inRange(e.at, range));
    const otherBox = odd.length
      ? box(`Other travel lines (${num(odd.length)})`, [
          table(odd.slice(-100).reverse(), [
            { label: 'When', get: (e) => tct(e.at), sort: (e) => e.at },
            { label: 'Line', get: (e) => e.title },
          ], { initial: 0 }),
          h('p', { class: 'muted small' }, 'Lines in Torn’s Travel category that aren’t a flight, a landing or a buy — shown as Torn wrote them, not counted.'),
        ], { id: 'travel-other' })
      : null;

    const notes = h(
      'p',
      { class: 'muted small', 'data-travel-notes': '' },
      'How it’s read: Torn doesn’t document its travel log lines, so each is read by its title and what it says. A trip is a flight out to a country, landing there, flying back and landing home — parts your log doesn’t show stay blank. A buy that doesn’t say where counts in the country you’d flown to. “Worth today” and “Profit (est.)” are estimates: what you bought at Torn’s market value today (not what you sold it for), less what you paid and the tickets your log shows. The Trading page’s Profit tab has what you really sold for.',
    );
    out.replaceChildren(...([kept, rangePicker(range, onRange), filters, tiles, flightsChart, profitChart, countryBox, itemBox, tripsBox, otherBox, notes].filter(Boolean) as HTMLElement[]));
  }

  void (async () => {
    try {
      // Torn's items (names, market values — the server's list, none of your calls).
      items = new Map((await ref('items')).data.map((i) => [i.id, i]));
    } catch {
      /* names show as "Item 206", values as — */
    }
    try {
      const r = await ensureReader();
      first ??= firstRead(r).then(
        (text) => void (logNote.textContent = text),
        (err: unknown) => void logNote.replaceChildren(problem(err)),
      );
      await first;
      if (out.isConnected) await fill();
    } catch (err) {
      out.replaceChildren(problem(err));
    }
  })();
  return out;
}
